import React, { useState, useEffect, useCallback } from 'react';
import { Form, Radio, Input, DatePicker, FormInstance, RadioChangeEvent, Row, Col } from 'antd';
import { InputCommon, ButtonCommon, SelectCommon, showAlertNotification } from '..';
import { formatPrice } from 'utils';
import { CalendarOutlined, DownOutlined } from '@ant-design/icons';
import { PaymentType } from 'models';
import dayjs from 'dayjs';
import { TransactionMethodCode } from 'models/TransactionMethod';
import { Bank, Money } from '@phosphor-icons/react';
import { useCart } from 'stores/cartPOSContext';

interface PaymentMethodPanelProps {
  form: FormInstance;
  totalAmount: number;
  onFinish?: () => void;
  onBack?: () => void;
  memberDiscount?: number;
  transactionMethod: TransactionMethodCode;
  setTransactionMethod: React.Dispatch<React.SetStateAction<TransactionMethodCode>>;
}

const PaymentMethodPanel: React.FC<PaymentMethodPanelProps> = ({
  form,
  totalAmount,
  onFinish,
  onBack,
  memberDiscount = 0,
  transactionMethod,
  setTransactionMethod,
}) => {
  const { getActiveCart, updateCart } = useCart();
  const activeCart = getActiveCart();
  if (onBack) console.log('');

  // Get payment data from cart or initialize it
  const getInitialPaymentData = () => {
    return (
      activeCart?.paymentData || {
        paymentType: PaymentType.FULL_PAYMENT,
        discountType: 'VND',
        discountValue: 0,
        vatType: 'percent',
        vatValue: 0,
        prepaidAmount: 0,
        extraFee: 0,
      }
    );
  };

  const [paymentType, setPaymentType] = useState<PaymentType>(getInitialPaymentData().paymentType);
  const [finalAmount, setFinalAmount] = useState<number>(totalAmount);
  const [prepaidAmount, setPrepaidAmount] = useState<number>(getInitialPaymentData().prepaidAmount || 0);
  const [extraFee, setExtraFee] = useState<number>(getInitialPaymentData().extraFee || 0);
  const [remainingAmount, setRemainingAmount] = useState<number>(0);
  const [discountValue, setDiscountValue] = useState<number>(getInitialPaymentData().discountValue || 0);
  const [vatValue, setVatValue] = useState<number>(getInitialPaymentData().vatValue || 0);

  // Reset state when cart changes
  useEffect(() => {
    if (activeCart) {
      const paymentData = getInitialPaymentData();

      // Update local state
      setPaymentType(paymentData.paymentType);
      setPrepaidAmount(paymentData.prepaidAmount || 0);
      setExtraFee(paymentData.extraFee || 0);
      setDiscountValue(paymentData.discountValue || 0);
      setVatValue(paymentData.vatValue || 0);

      // Update form values
      form.setFieldsValue({
        paymentType: paymentData.paymentType,
        discountType: paymentData.discountType || 'VND',
        discountValue: paymentData.discountValue || 0,
        vatType: paymentData.vatType || 'percent',
        vatValue: paymentData.vatValue || 0,
        prepaidAmount: paymentData.prepaidAmount || 0,
        extraFee: paymentData.extraFee || 0,
      });
    }
  }, [activeCart?.id, form]);

  // Save payment data to cart when it changes
  const savePaymentDataToCart = useCallback(() => {
    if (activeCart) {
      const paymentData = {
        paymentType: form.getFieldValue('paymentType'),
        discountType: form.getFieldValue('discountType') || 'VND',
        discountValue: Number(discountValue),
        vatType: form.getFieldValue('vatType') || 'percent',
        vatValue: Number(vatValue),
        prepaidAmount: Number(form.getFieldValue('prepaidAmount') || 0),
        extraFee: Number(form.getFieldValue('extraFee') || 0),
        finalAmount: Number(finalAmount),
        remainingAmount: Number(remainingAmount),
      };

      updateCart(activeCart.id, { paymentData });
    }
  }, [
    activeCart,
    updateCart,
    paymentType,
    discountValue,
    vatValue,
    prepaidAmount,
    extraFee,
    finalAmount,
    remainingAmount,
    form,
  ]);

  // Initialize on first load
  useEffect(() => {
    const storedPaymentType = form.getFieldValue('paymentType');
    if (storedPaymentType) {
      setPaymentType(storedPaymentType);
    } else {
      form.setFieldsValue({ paymentType: PaymentType.FULL_PAYMENT });
    }
  }, []);

  const recalculateTotalAmount = useCallback(() => {
    const values = form.getFieldsValue();
    const discountType = values.discountType || 'VND';
    const discountVal = parseFloat(values.discountValue) || 0;
    const vatType = values.vatType || 'percent';
    const vatVal = parseFloat(values.vatValue) || 0;

    let calculatedAmount = totalAmount;
    if (discountType === 'PERCENT') {
      calculatedAmount -= (calculatedAmount * discountVal) / 100;
    } else {
      calculatedAmount -= discountVal;
    }
    if (memberDiscount > 0) {
      calculatedAmount -= (calculatedAmount * memberDiscount) / 100;
    }
    if (vatType === 'percent') {
      calculatedAmount += (calculatedAmount * vatVal) / 100;
    } else {
      calculatedAmount += vatVal;
    }

    calculatedAmount = Math.max(0, Math.round(calculatedAmount));
    setFinalAmount(calculatedAmount);

    if (paymentType === PaymentType.FULL_PAYMENT) {
      setPrepaidAmount(calculatedAmount);
      setRemainingAmount(0);
      form.setFieldsValue({
        prepaidAmount: calculatedAmount as number,
        remainingAmount: calculatedAmount,
      });
    } else if (paymentType === PaymentType.PARTIAL_PAYMENT_FLEX) {
      console.log('Partial payment flex');

      const currentPrepaid = parseFloat(form.getFieldValue('prepaidAmount')) || 0;
      console.log('Current prepaid:', currentPrepaid);
      const remaining = Math.max(0, calculatedAmount - currentPrepaid);
      console.log('Remaining:', remaining);
      setRemainingAmount(remaining);
      form.setFieldsValue({ remainingAmount: remaining });
    } else if (paymentType === PaymentType.NEGOTIATED_PAYMENT) {
      setPrepaidAmount(0);
      setRemainingAmount(calculatedAmount);
      form.setFieldsValue({
        prepaidAmount: 0,
        remainingAmount: calculatedAmount,
      });
    }

    // Save the updated data to the cart
    savePaymentDataToCart();
  }, [form, totalAmount, paymentType, memberDiscount, savePaymentDataToCart, activeCart?.items.length]);

  const handlePaymentTypeChange = (e: RadioChangeEvent) => {
    const value = e.target.value;
    setPaymentType(value);
    if (value === PaymentType.FULL_PAYMENT) {
      setPrepaidAmount(finalAmount);
      setExtraFee(0);
      setRemainingAmount(0);
      form.setFieldsValue({
        prepaidAmount: finalAmount,
        remainingAmount: 0,
        extraFee: 0,
        paymentDueDate: null,
      });
    } else if (value === PaymentType.PARTIAL_PAYMENT_FLEX) {
      setPrepaidAmount(0);
      setRemainingAmount(finalAmount);
      form.setFieldsValue({
        prepaidAmount: 0,
        extraFee: 0,
        remainingAmount: finalAmount,
        paymentDueDate: null,
      });
    } else if (value === PaymentType.NEGOTIATED_PAYMENT) {
      setPrepaidAmount(0);
      setRemainingAmount(finalAmount);
      form.setFieldsValue({
        prepaidAmount: 0,
        remainingAmount: finalAmount,
        paymentDueDate: null,
      });
    }

    // Save changes to cart
    setTimeout(savePaymentDataToCart, 0);
  };

  const handlePrepaidAmountChange = (input: React.ChangeEvent<HTMLInputElement> | number) => {
    const raw = typeof input === 'number' ? input : parseFloat(input.target.value);
    const value = isNaN(raw) ? 0 : raw;
    const validPrepaid = Math.min(value, finalAmount);
    setPrepaidAmount(validPrepaid);
    const remaining = Math.max(0, finalAmount - validPrepaid);
    setRemainingAmount(remaining);
    form.setFieldsValue({ prepaidAmount: validPrepaid, remainingAmount: remaining });
    setTimeout(savePaymentDataToCart, 0);
  };

  const handleExtraFeeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value) || 0;

    setExtraFee(value);
    form.setFieldsValue({ extraFee: value });
    setTimeout(savePaymentDataToCart, 0);
  };

  const handleVatValueChange = (value: string) => {
    const vatType = form.getFieldValue('vatType') || 'percent';
    let vatValue = parseFloat(value) || 0;

    if (vatType === 'percent') {
      vatValue = Math.min(vatValue, 100);
    } else {
      vatValue = Math.min(vatValue, totalAmount * 2);
    }

    form.setFieldsValue({ vatValue: vatValue });
    setVatValue(vatValue);
    recalculateTotalAmount();
  };

  const handleDiscountValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const discountType = form.getFieldValue('discountType') || 'VND';
    let value = parseFloat(e.target.value) || 0;

    if (discountType === 'PERCENT') {
      value = Math.min(value, 100);
    } else {
      value = Math.min(value, totalAmount);
    }

    form.setFieldsValue({ discountValue: value });
    setDiscountValue(value);
    recalculateTotalAmount();
  };

  const handleDiscountTypeChange = (value: string) => {
    const currentValue = parseFloat(form.getFieldValue('discountValue')) || 0;
    let validValue = currentValue;

    if (value === 'PERCENT') {
      validValue = Math.min(currentValue, 100);
    } else {
      validValue = Math.min(currentValue, totalAmount);
    }

    form.setFieldsValue({
      discountType: value,
      discountValue: validValue,
    });

    setDiscountValue(validValue);
    recalculateTotalAmount();
  };

  const handleVatTypeChange = (value: string) => {
    const currentValue = parseFloat(form.getFieldValue('vatValue')) || 0;
    let validValue = currentValue;

    if (value === 'percent') {
      validValue = Math.min(currentValue, 100);
    }

    form.setFieldsValue({
      vatType: value,
      vatValue: validValue,
    });

    setVatValue(validValue);
    recalculateTotalAmount();
  };

  useEffect(() => {
    recalculateTotalAmount();
  }, [totalAmount, discountValue, vatValue, memberDiscount]);

  useEffect(() => {
    if (paymentType === PaymentType.FULL_PAYMENT) {
      setPrepaidAmount(finalAmount);
      setRemainingAmount(0);
      setExtraFee(0);
      form.setFieldsValue({
        prepaidAmount: finalAmount,
        remainingAmount: 0,
      });
    } else if (paymentType === PaymentType.PARTIAL_PAYMENT_FLEX) {
      const remaining = Math.max(0, finalAmount - prepaidAmount);
      setRemainingAmount(remaining);
      form.setFieldsValue({ remainingAmount: remaining });
    }

    savePaymentDataToCart();
  }, [paymentType, finalAmount]);
  useEffect(() => {
    if (activeCart && finalAmount !== undefined) {
      if (prepaidAmount > finalAmount) {
        if (paymentType === PaymentType.FULL_PAYMENT) {
          setPrepaidAmount(finalAmount);
          setExtraFee(0);
          setRemainingAmount(0);
          form.setFieldsValue({
            prepaidAmount: finalAmount,
            remainingAmount: 0,
            extraFee: 0,
          });
        } else if (paymentType === PaymentType.PARTIAL_PAYMENT_FLEX) {
          setPrepaidAmount(finalAmount);
          setRemainingAmount(0);
          setExtraFee(0);
          form.setFieldsValue({
            prepaidAmount: finalAmount,
            remainingAmount: 0,
            extraFee: 0,
          });
        }

        setTimeout(savePaymentDataToCart, 0);
      }
    }
  }, [activeCart?.items.length, activeCart?.totalAmount, finalAmount, paymentType]);

  useEffect(() => {
    const paymentData = getInitialPaymentData();
    console.log('Initial payment data:', paymentData);
    if (paymentData.deposit) {
      handlePrepaidAmountChange(Number(paymentData.deposit) || 0);
    }
  }, []);

  const getButtonText = () => {
    switch (paymentType) {
      case PaymentType.FULL_PAYMENT:
        return 'Hoàn tất đơn hàng';
      case PaymentType.PARTIAL_PAYMENT_FLEX:
        return 'Hoàn tất & tạo công nợ';
      case PaymentType.NEGOTIATED_PAYMENT:
        return 'Hoàn tất & tạo công nợ';
      default:
        return 'Hoàn tất đơn hàng';
    }
  };
  return (
    <div className='mt-6'>
      <h3 className='text-lg font-bold mb-3'>Thông tin thanh toán</h3>

      <div className='bg-black/5 p-4 rounded-lg'>
        <Row gutter={[16, 16]}>
          <Col span={12}>
            {paymentType === PaymentType.FULL_PAYMENT && (
              <Form.Item name='prepaidAmount' hidden={true}>
                <Input />
              </Form.Item>
            )}
            <Form.Item name='paymentType'>
              <Radio.Group
                onChange={handlePaymentTypeChange}
                value={paymentType}
                className='w-full flex flex-col custom-group-radio custom-radio gap-3 radio-border-primary'
              >
                <Radio
                  value={PaymentType.FULL_PAYMENT}
                  className={`w-full border rounded-2xl p-3 bg-white flex items-start ${
                    paymentType === PaymentType.FULL_PAYMENT ? 'border-blue-500' : 'border-black/10'
                  }`}
                >
                  <div className='ml-2'>
                    <div className='font-medium title-bold'>Thanh toán toàn bộ</div>
                    <div className='text-black/60 text-sm'>Thanh toán tổng số tiền của tất cả sản phẩm</div>
                  </div>
                </Radio>
                <Radio
                  value={PaymentType.PARTIAL_PAYMENT_FLEX}
                  className={`w-full border rounded-2xl p-3 bg-white flex items-start ${
                    paymentType === PaymentType.PARTIAL_PAYMENT_FLEX ? 'border-blue-500' : 'border-black/10'
                  }`}
                >
                  <div className='ml-2'>
                    <div className='font-medium title-bold'>Thanh toán tùy chọn</div>
                    <div className='text-black/60 text-sm'>Thanh toán tùy chọn số tiền sản phẩm</div>
                  </div>
                </Radio>
                {/* <Radio
                  value={PaymentType.NEGOTIATED_PAYMENT}
                  className={`w-full border rounded-2xl p-3 bg-white flex items-start ${
                    paymentType === PaymentType.NEGOTIATED_PAYMENT ? 'border-blue-500' : 'border-black/10'
                  }`}
                >
                  <div className='ml-2'>
                    <div className='font-medium title-bold'>Thanh toán sau</div>
                    <div className='text-black/60 text-sm'>Chuyển sang công nợ</div>
                  </div>
                </Radio> */}
              </Radio.Group>
            </Form.Item>
          </Col>

          <Col span={12} className='!p-4 border rounded-xl bg-white flex flex-col justify-between min-h-[430px]'>
            <div className='flex flex-col gap-3'>
              <div className='items-center grid grid-cols-[auto_minmax(0,1fr)_auto_auto] gap-2'>
                <div className='text-black/60 font-bold w-[120px]'>Tạm tính</div>
                <div className='flex items-center'>
                  <span className='font-bold'>{formatPrice(totalAmount)}</span>
                  <span className='text-sm text-black/60 font-bold ml-1'>VND</span>
                </div>
              </div>
              <div className='items-center grid grid-cols-[auto_minmax(0,1fr)_auto_auto] gap-2'>
                <div className='text-black/60 font-bold w-[120px]'>Giảm hạng thẻ</div>
                <span className='font-bold'>{memberDiscount}%</span>
              </div>
              <Form.Item name='discount' className='mb-2'>
                <div style={{ width: '100%' }} className='grid grid-cols-[auto_minmax(0,1fr)_auto_auto] gap-2'>
                  <Form.Item name='discountType' noStyle initialValue='VND'>
                    <SelectCommon
                      sizeSelect='sm'
                      options={[
                        { label: 'Giảm giá %', value: 'PERCENT' },
                        { label: 'Giảm giá VNĐ', value: 'VND' },
                      ]}
                      className='!w-[140px]'
                      placeholder='Chọn loại'
                      suffixIcon={<DownOutlined />}
                      onChange={handleDiscountTypeChange}
                    />
                  </Form.Item>
                  <Form.Item name='discountValue' noStyle initialValue={0}>
                    <InputCommon
                      type='number'
                      hardSize='sm'
                      placeholder='Nhập giá trị'
                      className='flex-1'
                      onChange={handleDiscountValueChange}
                      min={0}
                      max={form.getFieldValue('discountType') === 'PERCENT' ? 100 : undefined}
                    />
                  </Form.Item>
                </div>
              </Form.Item>
              <Form.Item name='vat' className='mb-2'>
                <div style={{ width: '100%' }} className='grid grid-cols-[auto_minmax(0,1fr)_auto_auto] gap-2'>
                  <Form.Item name='vatType' noStyle initialValue='percent'>
                    <SelectCommon
                      sizeSelect='sm'
                      options={[
                        { label: 'VAT %', value: 'percent' },
                        // { label: 'VAT VNĐ', value: 'amount' },
                      ]}
                      placeholder='Chọn loại'
                      className='!w-[140px]'
                      suffixIcon={<DownOutlined />}
                      onChange={handleVatTypeChange}
                    />
                  </Form.Item>
                  <Form.Item name='vatValue' noStyle initialValue={0}>
                    <SelectCommon
                      sizeSelect='sm'
                      options={[
                        { label: '0%', value: 0 },
                        { label: '5%', value: 5 },
                        { label: '8%', value: 8 },
                        { label: '10%', value: 10 },
                      ]}
                      defaultValue={0}
                      placeholder='Chọn loại'
                      className='flex-1'
                      suffixIcon={<DownOutlined />}
                      onChange={handleVatValueChange}
                    />
                    {/* <InputCommon
                      type='number'
                      hardSize='sm'
                      placeholder='---'
                      className='flex-1'
                      onChange={handleVatValueChange}
                      min={0}
                      max={form.getFieldValue('vatType') === 'percent' ? 100 : undefined}
                    /> */}
                  </Form.Item>
                </div>
              </Form.Item>
              <div className='grid grid-cols-[auto_minmax(0,1fr)_auto_auto] gap-2 items-center hidden'>
                <div className='text-black/60 font-bold w-[120px]'>Phụ phí</div>
                <Form.Item name='extraFee' className='flex-1 mb-0'>
                  <InputCommon
                    type='money'
                    hardSize='sm'
                    placeholder='Nhập vào số tiền phụ phí'
                    onChange={handleExtraFeeChange}
                  />
                </Form.Item>
              </div>

              <div className='items-center grid grid-cols-[auto_minmax(0,1fr)_auto_auto] gap-2 '>
                <div className='text-black/60 font-bold w-[120px]'>Tổng tiền</div>
                <div
                  className={
                    'flex items-center ' +
                    (paymentType === PaymentType.FULL_PAYMENT
                      ? 'text-[var(--sematicSuccessColor500)]'
                      : 'text-[var(--textBlack100)]')
                  }
                >
                  <span className='font-bold'>{formatPrice(finalAmount + extraFee)}</span>
                  <span className='text-sm opacity-60 font-bold ml-1'>VND</span>
                </div>
              </div>
              {paymentType === PaymentType.PARTIAL_PAYMENT_FLEX && (
                <>
                  <div className='grid grid-cols-[auto_minmax(0,1fr)_auto_auto] gap-2 items-center'>
                    <div className='text-black/60 font-bold w-[120px]'>Trả trước</div>
                    <Form.Item name='prepaidAmount' className='flex-1 mb-0'>
                      <InputCommon
                        type='money'
                        hardSize='sm'
                        placeholder='Nhập vào số tiền'
                        onChange={handlePrepaidAmountChange}
                      />
                    </Form.Item>
                  </div>

                  <div className='grid grid-cols-[auto_minmax(0,1fr)_auto_auto] gap-2 items-center'>
                    <div className='text-black/60 font-bold w-[120px]'>Còn lại</div>
                    <div className='flex items-center text-[var(--textBlack100)]'>
                      <span className='font-bold'>{formatPrice(remainingAmount)}</span>
                      <span className='text-sm opacity-60 font-bold ml-1'>VND</span>
                    </div>
                  </div>
                </>
              )}

              {(paymentType === PaymentType.PARTIAL_PAYMENT_FLEX || paymentType === PaymentType.NEGOTIATED_PAYMENT) && (
                <div className='grid grid-cols-[auto_minmax(0,1fr)_auto_auto] gap-2 items-center'>
                  <div className='text-black/60 font-bold w-[120px]'>Thời gian</div>
                  <Form.Item
                    rules={
                      remainingAmount !== 0
                        ? [
                            { required: true, message: 'Vui lòng chọn thời gian thanh toán' },
                            ({ getFieldValue }) => ({
                              validator(_, value) {
                                const createdAtVal = getFieldValue('createdAt');
                                if (!value || !createdAtVal) return Promise.resolve();
                                const due = dayjs(value).startOf('day');
                                const created = dayjs(createdAtVal).startOf('day');
                                return due.diff(created, 'day') >= 0
                                  ? Promise.resolve()
                                  : Promise.reject(
                                      new Error('Thời gian trả phần còn lại phải lớn hơn hoặc bằng thời gian lên đơn'),
                                    );
                              },
                            }),
                          ]
                        : []
                    }
                    name='paymentDueDate'
                    className='flex-1 mb-0'
                    getValueProps={(value) => ({
                      value: value ? dayjs(value) : null,
                    })}
                  >
                    <DatePicker
                      placeholder={
                        paymentType === PaymentType.NEGOTIATED_PAYMENT
                          ? 'Thời gian thanh toán sau'
                          : 'Thời gian trả phần còn lại'
                      }
                      format='DD/MM/YYYY'
                      className='w-full'
                      suffixIcon={<CalendarOutlined />}
                      disabledDate={(current) => {
                        if (!current) return false;
                        const todayStart = dayjs().startOf('day');
                        const createdAtVal = form.getFieldValue('createdAt');
                        const createdStart = createdAtVal ? dayjs(createdAtVal).startOf('day') : null;
                        const minAllowed = createdStart && createdStart.isAfter(todayStart) ? createdStart : todayStart;
                        return current < minAllowed;
                      }}
                    />
                  </Form.Item>
                </div>
              )}
              {paymentType === PaymentType.PARTIAL_PAYMENT_FLEX && (
                <div className='grid grid-cols-[auto_minmax(0,1fr)_auto_auto] gap-2 items-center'>
                  <div className='text-black/60 font-bold w-[120px]'>Thanh toán</div>
                  <div className='flex items-center text-[var(--sematicSuccessColor500)]'>
                    <span className='font-bold'>{formatPrice(prepaidAmount + extraFee)}</span>
                    <span className='text-sm opacity-60 font-bold ml-1'>VND</span>
                  </div>
                </div>
              )}
              {paymentType === PaymentType.NEGOTIATED_PAYMENT && (
                <div className='grid grid-cols-[auto_minmax(0,1fr)_auto_auto] gap-2 items-center'>
                  <div className='text-black/60 font-bold w-[120px]'>Công nợ</div>
                  <div className='flex items-center text-[var(--dangerColor500)]'>
                    <span className='font-bold'>{formatPrice(finalAmount)}</span>
                    <span className='text-sm opacity-60 font-bold ml-1'>VND</span>
                  </div>
                </div>
              )}
              <Form.Item name='remainingAmount' hidden={true}>
                <Input />
              </Form.Item>
              <Form.Item name='status' hidden>
                <Input />
              </Form.Item>
            </div>
            <div className=''>
              <div className='flex gap-3 mt-4'>
                <ButtonCommon
                  color={transactionMethod === 'CASH' ? 'primary' : 'soft'}
                  radius='rounded'
                  className='!w-[50%] h-10'
                  onClick={() => setTransactionMethod('CASH')}
                  icon={<Money size={20} />}
                >
                  Tiền mặt
                </ButtonCommon>

                <ButtonCommon
                  color={transactionMethod === 'BANK_TRANSFER' ? 'primary' : 'soft'}
                  radius='rounded'
                  className='!w-[50%] h-10'
                  onClick={() => setTransactionMethod('BANK_TRANSFER')}
                  icon={<Bank size={20} />}
                >
                  Chuyển khoản
                </ButtonCommon>
              </div>
              <div className='flex gap-3 mt-4'>
                <ButtonCommon
                  color='dashError'
                  radius='rounded'
                  className='!w-[25%] h-10'
                  onClick={() => {
                    form
                      .validateFields()
                      .then(() => {
                        form.setFieldsValue({ status: 'draft' });
                        if (onFinish) onFinish();
                      })
                      .catch((errorInfo) => {
                        showAlertNotification({
                          type: 'warning',
                          title: 'Thông báo',
                          message: 'Vui lòng nhập đúng thông tin thanh toán',
                        });
                        console.log('Validation failed:', errorInfo);
                      });
                  }}
                >
                  Lưu nháp
                </ButtonCommon>
                <ButtonCommon
                  color={paymentType === PaymentType.FULL_PAYMENT ? 'primary' : 'danger'}
                  radius='rounded'
                  className='!w-[75%] h-10'
                  onClick={() => {
                    form
                      .validateFields()
                      .then(() => {
                        if (onFinish) {
                          console.log(extraFee);
                          onFinish();
                        }
                      })
                      .catch((errorInfo) => {
                        showAlertNotification({
                          type: 'warning',
                          title: 'Thông báo',
                          message: 'Vui lòng nhập đúng thông tin thanh toán',
                        });
                        console.log('Validation failed:', errorInfo);
                      });
                  }}
                >
                  {getButtonText()}
                </ButtonCommon>
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default PaymentMethodPanel;
